import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Container } from "@/components/layout/Container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/auth/FormField";
import { useCurrentUser, useUpdateMe } from "@/hooks/useCurrentUser";
import { AddParentStep } from "@/components/onboarding/AddParentStep";
import { isParentPortalEnabled } from "@/lib/env";
import { cn } from "@/lib/cn";

const CLASSES = ["8", "9", "10", "11", "12"] as const;

const schema = z.object({
  class_id: z.enum(CLASSES, {
    errorMap: () => ({ message: "Pick your class" }),
  }),
  phone: z
    .string()
    .trim()
    .min(1, "Enter your mobile number")
    .refine((v) => /^[6-9]\d{9}$/.test(v), {
      message: "Enter a 10-digit Indian mobile number",
    }),
  school_name: z.string().trim().min(2, "Enter your school name").max(120),
  city: z.string().trim().max(80).optional(),
  parent_contact: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^[6-9]\d{9}$/.test(v), {
      message: "Enter a 10-digit number, or leave it blank",
    }),
});

type FormValues = z.infer<typeof schema>;

// "class-11" -> "11"
function gradeFromClassId(classId?: string | null): (typeof CLASSES)[number] | undefined {
  if (!classId) return undefined;
  const n = classId.replace(/^class-/, "");
  return (CLASSES as readonly string[]).includes(n) ? (n as (typeof CLASSES)[number]) : undefined;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Carry-through deep-link from the auth flow. Same-origin only.
  const nextRaw = params.get("next");
  const next =
    nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//")
      ? nextRaw
      : "/dashboard";
  // Edit mode lets an already-onboarded (but not-yet-subscribed) user change
  // their class via the "Change class" link on /subscribe. Without it the
  // early-return below would bounce them straight back out.
  const editMode = params.get("edit") === "1";
  const { data: user } = useCurrentUser();
  const updateMe = useUpdateMe();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const selectedClass = watch("class_id");

  // In edit mode, prefill the form from the existing profile once it loads.
  useEffect(() => {
    if (!editMode || !user) return;
    reset({
      class_id: gradeFromClassId(user.class_id),
      phone: user.phone ? user.phone.replace(/^\+91/, "") : "",
      school_name: user.school_name ?? "",
      city: user.city ?? "",
      parent_contact: user.parent_contact ? user.parent_contact.replace(/^\+91/, "") : "",
    });
  }, [editMode, user, reset]);

  // Already onboarded and not explicitly editing - bounce to where they were
  // heading (or dashboard). The RequireAuth gate enforces onboarding for users
  // without a class_id; this is the secondary guard.
  if (user && user.class_id && !editMode) {
    navigate(next, { replace: true });
    return null;
  }

  // Both CTAs validate + persist the same profile patch, then route differently.
  function saveThen(target: string) {
    return handleSubmit(async (values) => {
      setSubmitting(true);
      try {
        const patch: Partial<{
          class_id: string;
          phone: string;
          school_name: string;
          city: string | null;
          parent_contact: string | null;
        }> = {
          class_id: `class-${values.class_id}`,
          phone: values.phone,
          school_name: values.school_name,
        };
        patch.city = values.city ? values.city : null;
        patch.parent_contact = values.parent_contact ? values.parent_contact : null;
        await updateMe.mutateAsync(patch);
        navigate(target, { replace: true });
      } catch (err) {
        const message =
          (err as { response?: { data?: { status_message?: string } } }).response?.data
            ?.status_message ||
          (err as { message?: string }).message ||
          "Couldn't save your details.";
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    });
  }

  return (
    <Container size="md" className="py-12 lg:py-16">
      <div className="mx-auto max-w-xl">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Welcome{user?.user_name ? `, ${user.user_name.split(" ")[0]}` : ""}.
        </h1>
        <p className="mt-2 text-text-muted">
          Tell us a little about you so we can load the right curriculum. You can change anything later in Settings.
        </p>

        <form className="mt-8 flex flex-col gap-6" noValidate>
          {/* Class - required ------------------------------------------ */}
          <div>
            <p className="text-sm font-medium mb-3">Which class are you in?</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {CLASSES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setValue("class_id", c, { shouldValidate: true })}
                  className={cn(
                    "h-14 rounded-lg border text-center font-display text-lg font-semibold",
                    "transition-[border-color,background-color,box-shadow] duration-150",
                    selectedClass === c
                      ? "border-brand bg-brand-soft text-brand"
                      : "border-border hover:border-border-strong",
                  )}
                >
                  Class {c}
                </button>
              ))}
            </div>
            {errors.class_id ? (
              <p className="mt-2 text-sm text-danger">{errors.class_id.message}</p>
            ) : null}
          </div>

          {/* Details - phone + school required, rest optional ---------- */}
          {selectedClass ? (
            <Card className="p-5 sm:p-6 flex flex-col gap-4">
              <div>
                <p className="text-sm font-medium">A few details</p>
                <p className="text-xs text-text-muted mt-1">
                  We use these to personalise reminders, receipts and support. Phone and school are required.
                </p>
              </div>
              <FormField id="phone" label="Phone number" error={errors.phone?.message}>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="10-digit mobile number"
                  aria-invalid={!!errors.phone}
                  {...register("phone")}
                />
              </FormField>
              <FormField id="school_name" label="School" error={errors.school_name?.message}>
                <Input
                  id="school_name"
                  placeholder="Your school name"
                  aria-invalid={!!errors.school_name}
                  {...register("school_name")}
                />
              </FormField>
              <FormField id="city" label="City (optional)" error={errors.city?.message}>
                <Input id="city" autoComplete="address-level2" placeholder="Your city" {...register("city")} />
              </FormField>
              <FormField id="parent_contact" label="Parent contact (optional)" error={errors.parent_contact?.message}>
                <Input
                  id="parent_contact"
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit number"
                  {...register("parent_contact")}
                />
              </FormField>
            </Card>
          ) : null}

          {/* Add a parent - optional, only once a class is picked and only
              in edit-free first-run onboarding (not the /subscribe edit flow). */}
          {selectedClass && !editMode && isParentPortalEnabled() ? <AddParentStep /> : null}

          {/* Two paths: subscribe now, or browse the free demo ---------- */}
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              size="lg"
              loading={submitting}
              disabled={!selectedClass}
              onClick={saveThen("/subscribe")}
            >
              Subscribe & unlock everything
            </Button>
            <Button
              type="button"
              variant="secondary"
              loading={submitting}
              disabled={!selectedClass}
              onClick={saveThen(next)}
            >
              Browse demo content first
            </Button>
            <p className="text-xs text-text-muted text-center">
              On the demo you can explore sample chapters free. Subscribe any time to unlock your full class.
            </p>
          </div>
        </form>
      </div>
    </Container>
  );
}
